import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { Box, Typography, Button, IconButton, Collapse, Divider } from '@mui/material';
import TrainingIcon from '@mui/icons-material/School';
import CollapseIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import { post } from '../../../utils/apiClient';

interface TrainingPillProps {
  show: boolean;
  user: Record<string, unknown> | null;
}

/**
 * TRAINING pill shown in the header when training mode is active.
 *
 * Training mode means demo characters/data have been moved into a
 * sandbox for onboarding practice.  The pill provides:
 * - Quick status indicator (purple TRAINING badge)
 * - Character switching (same location as old demo pill)
 * - Option to exit training mode (selective wipe)
 */
const TrainingPill: React.FC<TrainingPillProps> = ({ show, user }) => {
  const isMobile = useIsMobile();
  const { isDarkMode } = useDarkMode();
  const { refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [wiping, setWiping] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isExpanded) {
        event.preventDefault();
        setIsExpanded(false);
        buttonRef.current?.focus();
      }
    }
    if (isExpanded) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isExpanded]);

  const handleSwitchCharacter = () => {
    setIsExpanded(false);
    navigate('/demo');
  };

  const handleExitTraining = useCallback(async () => {
    setWiping(true);
    try {
      // Wipe all clinical training data categories and exit training mode
      await post('/api/auth/selective-wipe', {
        categories: ['patients', 'encounters', 'notes', 'appointment_types', 'fee_schedules', 'note_templates', 'email_templates', 'automation_rules'],
        exit_training_mode: true,
      }, { credentials: 'include' });
      if (refreshAuth) {
        await refreshAuth();
      }
      window.location.reload();
    } catch {
      // Silently fail — user can retry
    } finally {
      setWiping(false);
    }
  }, [refreshAuth]);

  if (!show) return null;

  const clinicName = (user?.clinic_name as string) || 'your clinic';
  const pillLabel = isMobile ? 'T' : 'TRAINING';

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        className={`inline-flex items-center gap-0.5 rounded-full font-semibold bg-purple-200 text-purple-900 dark:bg-purple-700 dark:text-purple-100 transition-opacity duration-150 cursor-pointer hover:bg-purple-300 dark:hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400 ${isMobile ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-[10px]'}`}
        role="button"
        aria-expanded={isExpanded}
        aria-haspopup="dialog"
        aria-label="Training sandbox mode - click to expand"
        title="Training sandbox mode active"
        style={{ height: isMobile ? '22px' : 'var(--pill-height, 18px)' }}
      >
        {pillLabel}
        {isExpanded ? (
          <CollapseIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
        ) : (
          <ExpandIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
        )}
      </button>

      <Collapse in={isExpanded}>
        <Box
          sx={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '3.5rem' : 'auto',
            left: isMobile ? 'auto' : 0,
            right: isMobile ? '0.5rem' : 'auto',
            mt: isMobile ? 0 : 1,
            width: 'min(340px, calc(100vw - 16px))',
            minWidth: 0,
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: 3,
            backgroundColor: isDarkMode ? '#1e293b' : '#f3e8ff',
            border: `1px solid ${isDarkMode ? '#334155' : '#9333ea'}`,
            zIndex: 1300,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e9d5ff'}`,
          }}>
            <TrainingIcon color="secondary" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: isDarkMode ? '#f1f5f9' : 'inherit' }}>
              Training Sandbox Mode
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(false)}
              sx={{
                ml: 'auto',
                p: 0.5,
                color: isDarkMode ? '#94a3b8' : '#9333ea',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(147, 51, 234, 0.04)'
                }
              }}
              title="Collapse"
            >
              <CollapseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1, color: isDarkMode ? '#cbd5e1' : 'text.secondary' }}>
              Demo characters and sample data are available for practice and training in <strong>{clinicName}</strong>.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                • Switch between demo characters to practice different workflows
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                • Training data is separate from your real clinic data
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                • You can remove training data anytime
              </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, pt: 1, flexWrap: 'wrap', borderTop: `1px solid ${isDarkMode ? '#334155' : '#e9d5ff'}` }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SwapHoriz />}
                onClick={handleSwitchCharacter}
                sx={{
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0.5,
                  minHeight: '28px',
                  borderColor: isDarkMode ? '#64748b' : '#9333ea',
                  color: isDarkMode ? '#e2e8f0' : '#9333ea',
                  '&:hover': {
                    borderColor: isDarkMode ? '#94a3b8' : '#7e22ce',
                    backgroundColor: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(147, 51, 234, 0.04)'
                  }
                }}
              >
                Switch Character
              </Button>
            </Box>

            <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#334155' : '#e9d5ff' }} />
            <Button
              variant="text"
              size="small"
              startIcon={<DeleteSweep />}
              onClick={handleExitTraining}
              disabled={wiping}
              sx={{
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
                minHeight: '28px',
                color: isDarkMode ? '#94a3b8' : 'text.secondary',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {wiping ? 'Removing...' : 'Remove Training Data'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </div>
  );
};

export default TrainingPill;
